import React from 'react';

export interface InfoItem {
  isBold?: boolean;
  highlight?: boolean;
  label: string;
  labelEn?: string;
  value: React.ReactNode;
  valueClassName?: string;
  colSpan?: 1 | 2;
}

export interface PrintInfoGridProps {
  leftColumn?: (InfoItem | null | undefined | false)[];
  rightColumn?: (InfoItem | null | undefined | false)[];
  fullWidthItems?: (InfoItem | null | undefined | false)[];
  labelWidth?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Standard Print Info Grid (Clean Borderless 2-Column Tab-Aligned Key-Value Layout)
 * Replaces cumbersome Excel-like bordered tables with elegant, aligned typography.
 */
export default function PrintInfoGrid({
  leftColumn = [],
  rightColumn = [],
  fullWidthItems = [],
  labelWidth = 'w-[105px]',
  className = 'mb-4',
  children,
}: PrintInfoGridProps) {
  const validLeft = leftColumn.filter(Boolean) as InfoItem[];
  const validRight = rightColumn.filter(Boolean) as InfoItem[];
  const validFull = fullWidthItems.filter(Boolean) as InfoItem[];

  const maxRows = Math.max(validLeft.length, validRight.length);

  const renderItem = (item: InfoItem) => (
    <div
      className={`flex items-baseline text-[11px] leading-snug ${
        item.colSpan === 2 ? 'col-span-2' : ''
      }`}
    >
      <div
        className={`${labelWidth} shrink-0 text-slate-600 font-medium flex justify-between pr-2`}
      >
        <span>
          {item.label}
          {item.labelEn && (
            <i className="font-normal text-slate-400 text-[10px] ml-1">
              /{item.labelEn}
            </i>
          )}
        </span>
        <span className="font-normal text-slate-400">:</span>
      </div>
      <div
        className={`flex-1 font-bold text-slate-900 break-words ${
          item.valueClassName || ''
        }`}
      >
        {item.value || '—'}
      </div>
    </div>
  );

  return (
    <div className={`text-[11px] font-sans ${className}`}>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
        {Array.from({ length: maxRows }).map((_, idx) => {
          const leftItem = validLeft[idx];
          const rightItem = validRight[idx];

          return (
            <React.Fragment key={idx}>
              {leftItem ? renderItem(leftItem) : <div />}
              {rightItem ? renderItem(rightItem) : <div />}
            </React.Fragment>
          );
        })}

        {validFull.map((item, idx) => (
          <React.Fragment key={`full-${idx}`}>{renderItem(item)}</React.Fragment>
        ))}
      </div>
      {children}
    </div>
  );
}
